import ExpandButton from 'components/molecules/expand-button';
import { Link } from 'react-router-dom';
import { Routes } from 'router/routes';
import { useTranslation } from 'react-i18next';
import { languagesMap } from 'constants/languages';
import classNames from 'classnames';
import useUser from 'utils/useUser';
import ExclamationTriangleIcon from 'components/atoms/icons/exclamation-triangle';
import {
  stateAbbreviations,
  type ClinicDoNotCallRestrictionType,
  type DoNotCallRestrictionType,
  type Languages,
  type States,
  CroStatuses,
  OverviewDashboardStatus,
} from 'infrastructure/enums';
import Countdown from 'components/molecules/activity-table/components/countdown';
import { useOverviewDashboardFilter } from 'infrastructure/hooks/rpm/overview-dashboard';

import s from './styles.module.scss';
import DncTag from './dnc-tag';

import type { Nullable } from 'infrastructure/types';
import type { FC } from 'react';

interface PatientCellProps {
  guid: string;
  name: string;
  dob: string;
  expand: boolean;
  language: Languages;
  provider: string;
  croStatus?: CroStatuses | null;
  interactionStatus?: boolean;
  doNotCallRestrictionType?: Nullable<DoNotCallRestrictionType>;
  doNotCallStartTime?: Nullable<string>;
  doNotCallEndTime?: Nullable<string>;
  clinicDoNotCallRestrictionType?: Nullable<ClinicDoNotCallRestrictionType>;
  clinicDoNotCallStartTime?: Nullable<string>;
  clinicDoNotCallEndTime?: Nullable<string>;
  timezone?: Nullable<string>;
  actionedByAgencyUser?: boolean;
  onExpand: () => void;
  state?: States | null;
  slaDeadline?: number;
}

const PatientCell: FC<PatientCellProps> = ({
  guid,
  name,
  provider,
  dob,
  language,
  expand,
  croStatus,
  interactionStatus,
  onExpand,
  doNotCallRestrictionType,
  doNotCallStartTime,
  doNotCallEndTime,
  clinicDoNotCallRestrictionType,
  clinicDoNotCallStartTime,
  clinicDoNotCallEndTime,
  timezone,
  actionedByAgencyUser,
  state,
  slaDeadline,
}) => {
  const {
    isCriticalOrAtRiskAlertingRole,
    isAgencyDoctor,
    isPractice,
    hasClinicalRole,
    isFullyManagedAgency,
  } = useUser();
  const { t } = useTranslation();
  const showReviewedByAgencyBadge =
    isPractice &&
    hasClinicalRole &&
    isFullyManagedAgency &&
    actionedByAgencyUser;
  const {
    filter: { status },
  } = useOverviewDashboardFilter();
  const showCROBadge =
    croStatus && croStatus !== CroStatuses.False && isAgencyDoctor;

  return (
    <div className={s.wrapper}>
      <ExpandButton expanded={expand} onClick={onExpand} />
      <div className={s['patient-info-wrapper']}>
        <div className={s.patient}>
          <p
            className={classNames(s.name, {
              [s.disabled]: isCriticalOrAtRiskAlertingRole,
            })}
          >
            <Link to={Routes.patientGuid(guid)}>{name}</Link>
          </p>
          <div>
            <p className={s.date}>{dob}</p>
            <p className={s.language}>{languagesMap[language]}</p>
            <p className={s.state}>
              State:{' '}
              {state
                ? stateAbbreviations[`${state}`]
                : t('labels.bannerNoData')}
            </p>
          </div>

          <div className={s['tags-wrapper']}>
            {slaDeadline && status === OverviewDashboardStatus.Priority && (
              <Countdown slaDeadline={slaDeadline} />
            )}
            <DncTag
              doNotCallRestrictionType={doNotCallRestrictionType}
              doNotCallStartTime={doNotCallStartTime}
              doNotCallEndTime={doNotCallEndTime}
              clinicDoNotCallRestrictionType={clinicDoNotCallRestrictionType}
              clinicDoNotCallStartTime={clinicDoNotCallStartTime}
              clinicDoNotCallEndTime={clinicDoNotCallEndTime}
              patientTimezone={timezone}
            />
            <p
              className={classNames(
                s.tag,
                s['two-way-sync'],
                interactionStatus && s.sync,
              )}
            >
              {t('labels.2waySync')} -{' '}
              {interactionStatus ? t('labels.yes') : t('labels.no')}
            </p>
            {showCROBadge && (
              <p className={classNames(s.tag, s.cro)}>
                {t('labels.cro')}
                <ExclamationTriangleIcon />
              </p>
            )}
            {showReviewedByAgencyBadge && (
              <p className={classNames(s.tag, s['agency-review'])}>
                {t('labels.reviewedByAgency')}
              </p>
            )}
          </div>
        </div>
        <div className={s.provider}>
          {t('labels.provider')}: <span>{provider}</span>
        </div>
      </div>
    </div>
  );
};
export default PatientCell;
